import { useState } from "react";
import { Box } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { FS } from "@plannel/utils/font-util";

// 수요 우선순위를 saas-ai(AiMpScenarioPlanning) 스타일 번호 칩 리스트로 표시한다.
//   · onReorder 가 있으면 드래그로 순서 변경(편집), 없으면 읽기전용.
//   · 항목 shape 은 자유 — keyField(라벨 i18n 키)·sortingField(asc/desc) 만 지정하면 된다.
//   · lockedIndex 로 지정한 행은 우선순위 0 고정 — 번호 0 + 자물쇠, 드래그/드롭 대상 제외.
//     (MpSettings 의 Appliance 0번 고정행과 동일 규격. MP 는 날짜 항목이 여기에 해당한다)
//   · accent 는 앱 브랜드 teal. MpSettings 의 풀↔적용 빌더 칩과 동일 톤.
const ACC = "#048BA8";
const DESC_RED = "#E53935";

// asc→▲ / desc→▼ — 설정·AI 화면이 갈라지지 않게 공용.
export const DirArrow = ({ sorting }) => (
  <Box component="span" sx={{ fontWeight: 700, lineHeight: 1, flexShrink: 0, color: sorting === "asc" ? ACC : DESC_RED }}>
    {sorting === "asc" ? "▲" : "▼"}
  </Box>
);

const DemandPriorityList = ({
  items = [],
  keyField = "priorityItem",
  sortingField = "sorting",
  onReorder,
  lockedIndex = -1,
  t,
  sx,
}) => {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const editable = typeof onReorder === "function";

  const move = (from, to) => {
    if (from == null || to == null || from === to) return;
    if (from === lockedIndex || to === lockedIndex) return; // 고정행은 자리를 내주지도, 옮기지도 않는다
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  // 바깥은 하나의 박스로 감싸고(teal 프레임), 항목은 개별 박스 없이 번호+텍스트+화살표만.
  return (
    <Box sx={{
      display: "flex", flexDirection: "column", gap: 0.25,
      border: `1px solid ${ACC}55`, borderRadius: "8px", p: 0.5, boxSizing: "border-box",
      ...sx,
    }}>
      {items.map((item, i) => {
        const locked = i === lockedIndex;
        const movable = editable && !locked;

        return (
        <Box
          key={`${item?.[keyField] ?? "row"}-${i}`}
          draggable={movable}
          onDragStart={movable ? () => setDragIdx(i) : undefined}
          onDragOver={movable ? (e => { e.preventDefault(); setOverIdx(i); }) : undefined}
          onDragLeave={movable ? (() => setOverIdx(p => (p === i ? null : p))) : undefined}
          onDrop={movable ? (e => { e.preventDefault(); move(dragIdx, i); setDragIdx(null); setOverIdx(null); }) : undefined}
          sx={{
            display: "flex", alignItems: "center", gap: 0.75, boxSizing: "border-box",
            px: "6px", py: "5px", borderRadius: "6px",
            border: "1px solid transparent", // 고정행 테두리와 높이를 맞춘다
            color: "text.primary", fontSize: FS.TITLE, fontWeight: 600,
            cursor: movable ? "grab" : "default",
            boxShadow: overIdx === i ? `inset 0 2px 0 0 ${ACC}` : "none",
            transition: "background-color .12s, box-shadow .12s",
            "&:hover": movable ? { bgcolor: `${ACC}0D` } : {},
            ...(locked ? { borderColor: ACC, bgcolor: `${ACC}24` } : {}),
          }}>
          <Box component="span" sx={{
            minWidth: 16, height: 16, px: "3px", borderRadius: "50%", bgcolor: ACC, color: "#fff",
            fontSize: FS.CAPTION, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            {locked ? 0 : (lockedIndex >= 0 && i > lockedIndex ? i : i + 1)}
          </Box>
          <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t ? t(item?.[keyField]) : item?.[keyField]}
            {locked && <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 0.5 }}>({t ? t("date") : "date"})</Box>}
          </Box>
          {locked
            ? <LockOutlinedIcon sx={{ fontSize: 14, color: ACC, flexShrink: 0 }} />
            : <DirArrow sorting={item?.[sortingField]} />}
        </Box>
        );
      })}
    </Box>
  );
};

export default DemandPriorityList;
